/**
 * Stakeholder Pulse Service
 *
 * Core logic for generating, sending, and recording responses for stakeholder
 * pulse notifications.
 *
 * generatePulsesForPrioritySet — called after a priority list is published.
 *   • For each item in the list that has a submitter, computes outcome and
 *     builds an outcome_summary, then upserts a stakeholder_pulses row.
 *   • Items with no submitter are skipped silently.
 *   • Safe to re-run (upsert on item_id × priority_set_id).
 *
 * This service is intentionally side-effect-only for the generation path —
 * it does NOT update local Zustand state. Callers should refresh the store
 * after generation if they need fresh UI data.
 */

import { getSafeSupabaseClient, isSupabaseAvailable, getCurrentWorkspaceId } from './supabaseSync';
import { getWorkspaceModules, isModuleEnabled } from './workspaceModulesService';
import type { PulseOutcome } from '../types/prioritization/stakeholderPulse';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface GeneratePulsesInput {
  prioritySetId: string;
  prioritySetName: string;
  workspaceId?: string;           // Falls back to getCurrentWorkspaceId()
  createdBy?: string;

  /** Ordered list of item IDs (position = rank, 1-based) */
  rankedItemIds: string[];

  /** Cut line position (1-based index; items at or above are committed) */
  cutLinePosition?: number;

  /** Optional publication message to incorporate in summaries */
  publicationMessage?: string;
}

export interface GeneratePulsesResult {
  generated: number;
  skipped: number;        // No submitter or already exists
  errors: string[];
}

// ── Generation ────────────────────────────────────────────────────────────────

/**
 * Generate stakeholder_pulses rows for all items in a newly-published
 * priority list.
 *
 * Called from prioritySetStore.publishPrioritySet (fire-and-forget after
 * governance event; failures do not block publication).
 */
export async function generatePulsesForPrioritySet(
  input: GeneratePulsesInput,
): Promise<GeneratePulsesResult> {
  if (!isSupabaseAvailable()) {
    return { generated: 0, skipped: 0, errors: ['Supabase unavailable'] };
  }

  const supabase = getSafeSupabaseClient()!;
  const workspaceId = input.workspaceId ?? getCurrentWorkspaceId();
  if (!workspaceId) {
    return { generated: 0, skipped: 0, errors: ['No workspace context'] };
  }

  // Only generate pulses if the Stakeholder Pulse module is enabled for this workspace.
  const { data: features } = await getWorkspaceModules(workspaceId);
  if (!isModuleEnabled(features, 'stakeholder_pulse')) {
    return { generated: 0, skipped: 0, errors: [] };
  }

  const result: GeneratePulsesResult = { generated: 0, skipped: 0, errors: [] };

  if (!input.rankedItemIds.length) return result;

  // Fetch items with their submitter UUID (created_by_user_id) and titles.
  // Note: created_by is a legacy display-name string; created_by_user_id is
  // the canonical auth UUID used everywhere else in the system.
  const { data: itemRows, error: itemsError } = await supabase
    .from('items')
    .select('id, title, created_by_user_id')
    .in('id', input.rankedItemIds);

  if (itemsError) {
    result.errors.push(`Failed to fetch items: ${itemsError.message}`);
    await writeGenerationEvent(supabase, input, result);
    return result;
  }

  const itemMap = new Map(
    (itemRows ?? []).map(r => [
      r.id as string,
      r as { id: string; title: string; created_by_user_id: string | null },
    ]),
  );

  const now = new Date().toISOString();

  for (let idx = 0; idx < input.rankedItemIds.length; idx++) {
    const itemId = input.rankedItemIds[idx];
    const rank = idx + 1;
    const item = itemMap.get(itemId);

    if (!item) {
      result.skipped++;
      continue;
    }

    const submitterId = item.created_by_user_id ?? null;
    if (!submitterId) {
      result.skipped++;
      continue;
    }

    const outcome = computeOutcome(rank, input.cutLinePosition, input.rankedItemIds.length);
    const outcomeSummary = buildOutcomeSummary({
      itemTitle: item.title,
      prioritySetName: input.prioritySetName,
      outcome,
      rank,
      totalItems: input.rankedItemIds.length,
      cutLinePosition: input.cutLinePosition,
      publicationMessage: input.publicationMessage,
    });

    const { error: upsertError } = await supabase
      .from('stakeholder_pulses')
      .upsert(
        {
          workspace_id: workspaceId,
          item_id: itemId,
          submitter_id: submitterId,
          priority_set_id: input.prioritySetId,
          outcome,
          rank,
          outcome_summary: outcomeSummary,
          status: 'pending',
          created_at: now,
          created_by: input.createdBy ?? null,
          updated_at: now,
        },
        { onConflict: 'item_id,priority_set_id', ignoreDuplicates: true },
      );

    if (upsertError) {
      result.errors.push(`Item ${itemId}: ${upsertError.message}`);
    } else {
      result.generated++;
    }
  }

  // Write a governance event so the facilitator can see exactly what happened
  // (generated count, skipped count, any per-item errors) without having to
  // infer from an empty table.
  await writeGenerationEvent(supabase, input, result);

  return result;
}

/**
 * Persist a STAKEHOLDER_PULSE_GENERATED governance event so generation
 * results are observable in the UI.  Best-effort — failures are swallowed.
 */
async function writeGenerationEvent(
  supabase: ReturnType<typeof getSafeSupabaseClient>,
  input: GeneratePulsesInput,
  result: GeneratePulsesResult,
): Promise<void> {
  if (!supabase) return;
  try {
    await supabase.from('governance_events').insert({
      id: crypto.randomUUID(),
      event_type: 'STAKEHOLDER_PULSE_GENERATED',
      occurred_at: new Date().toISOString(),
      actor_id: input.createdBy ?? null,
      priority_set_id: input.prioritySetId,
      after_state_snapshot: {
        generated: result.generated,
        skipped: result.skipped,
        errors: result.errors,
      },
    });
  } catch {
    // Non-blocking — generation result is already in the pulse rows themselves
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function computeOutcome(
  rank: number,
  cutLinePosition: number | undefined,
  _totalItems: number,
): PulseOutcome {
  if (cutLinePosition == null) {
    // No cut line — all ranked items are "committed"
    return 'above_cut_line';
  }
  return rank <= cutLinePosition ? 'above_cut_line' : 'below_cut_line';
}

interface OutcomeSummaryInput {
  itemTitle: string;
  prioritySetName: string;
  outcome: PulseOutcome;
  rank: number;
  totalItems: number;
  cutLinePosition?: number;
  publicationMessage?: string;
}

function buildOutcomeSummary(input: OutcomeSummaryInput): string {
  const { itemTitle, prioritySetName, outcome, rank, totalItems, publicationMessage } = input;

  let summary = '';

  if (outcome === 'above_cut_line') {
    summary =
      `Your idea "${itemTitle}" was ranked #${rank} out of ${totalItems} in ` +
      `"${prioritySetName}" and has been committed for action.`;
  } else if (outcome === 'below_cut_line') {
    summary =
      `Your idea "${itemTitle}" was ranked #${rank} out of ${totalItems} in ` +
      `"${prioritySetName}". It was considered but fell below the commitment threshold this cycle.`;
  } else {
    summary =
      `Your idea "${itemTitle}" was not included in the priority list ` +
      `"${prioritySetName}" in this cycle.`;
  }

  if (publicationMessage) {
    summary += ` Note from the team: ${publicationMessage}`;
  }

  return summary;
}
